import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";
import {MCATChatAuthInfo} from "../../services/mcatChat/types";
import { getCookie, setCookie } from "../../utils/functions/cookieUtils";

interface AuthState {
  token: string;
  mcatChatAuthInfo: MCATChatAuthInfo;
  mcatChatSelectedSubject: string;
  mcatChatDoNotShowTransitionWarning: boolean;
  mcatChatInitialContext: string;
  mcatChatSessionId: number | null;
  mcatChatCancelPendingTransition: boolean;
  showMcatChatErrorModal: boolean;
  mcatChatExamInfo: {
    examName: string;
    section: string;
    questionNumber: string;
  };
}

// Cookie name constant for the "do not show transition warning" preference
const MCAT_CHAT_DO_NOT_SHOW_WARNING_COOKIE = "mcatChatDoNotShowTransitionWarning";

// Helper function to read the initial value from cookie
// Also handles migration from localStorage to cookie for existing users
const getInitialDoNotShowWarning = (): boolean => {
  // First check if cookie exists
  const cookieValue = getCookie(MCAT_CHAT_DO_NOT_SHOW_WARNING_COOKIE);
  if (cookieValue !== null) {
    return cookieValue === "true";
  }
  
  // If no cookie exists, check localStorage for migration
  // (from previous redux-persist storage)
  try {
    const persistedAuth = localStorage.getItem("persist:auth");
    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);
      if (authData.mcatChatDoNotShowTransitionWarning) {
        const value = JSON.parse(authData.mcatChatDoNotShowTransitionWarning);
        if (value === true) {
          // Migrate to cookie
          setCookie(MCAT_CHAT_DO_NOT_SHOW_WARNING_COOKIE, "true", 3650);
          return true;
        }
      }
    }
  } catch (error) {
    console.error("Error migrating mcatChatDoNotShowTransitionWarning from localStorage to cookie:", error);
  }
  
  // Default to false if nothing found
  return false;
};

const initialState: AuthState = {
  token: "",
  mcatChatAuthInfo: {
    mcatChatToken: "",
    mcatChatSession: "",
    isMcatChatSessionEnd: false,
  },
  mcatChatSelectedSubject: "",
  mcatChatDoNotShowTransitionWarning: getInitialDoNotShowWarning(),
  mcatChatInitialContext: "",
  mcatChatSessionId: null,
  mcatChatCancelPendingTransition: false,
  showMcatChatErrorModal: false,
  mcatChatExamInfo: {
    examName: "",
    section: "",
    questionNumber: ""
  }
};

export const Auth = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setMcatChatAuthInfo: (state, action: PayloadAction<MCATChatAuthInfo>) => {
      const obj = {...state.mcatChatAuthInfo};
      if (action.payload.mcatChatToken) {
        obj['mcatChatToken'] = action.payload.mcatChatToken;
      } else if (action.payload.mcatChatSession) {
        obj['mcatChatSession'] = action.payload.mcatChatSession;
      }
      obj['isMcatChatSessionEnd'] = action.payload.isMcatChatSessionEnd;
      state.mcatChatAuthInfo = obj;
    },
    setMcatChatSelectedSubject: (state, action: PayloadAction<string>) => {
      state.mcatChatSelectedSubject = action.payload;
    },
    setMcatChatDoNotShowTransitionWarning: (state, action: PayloadAction<boolean>) => {
      state.mcatChatDoNotShowTransitionWarning = action.payload;
      // Store the preference in a cookie that expires in 10 years (essentially never)
      setCookie(MCAT_CHAT_DO_NOT_SHOW_WARNING_COOKIE, action.payload.toString(), 3650);
    },
    setMcatChatInitialContext: (state, action: PayloadAction<string>) => {
      state.mcatChatInitialContext = action.payload;
    },
    setMcatChatExamInfo: (state, action: PayloadAction<{examName: string; section: string; questionNumber: string}>) => {
      state.mcatChatExamInfo = action.payload;
    },
    setMcatChatSessionId: (state, action: PayloadAction<number | null>) => {
      state.mcatChatSessionId = action.payload;
    },
    setMcatChatCancelPendingTransition: (state, action: PayloadAction<boolean>) => {
      state.mcatChatCancelPendingTransition = action.payload;
    },
    setShowMcatChatErrorModal: (state, action: PayloadAction<boolean>) => {
      state.showMcatChatErrorModal = action.payload;
    },
    logOut: state => {
      state = initialState;
    },
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => {
      return {
        ...initialState,
        mcatChatDoNotShowTransitionWarning: getInitialDoNotShowWarning(),
      };
    });
  },
});
export const { setToken, logOut, setMcatChatAuthInfo, setMcatChatSelectedSubject, setMcatChatDoNotShowTransitionWarning, setMcatChatInitialContext, setMcatChatExamInfo, setMcatChatSessionId, setMcatChatCancelPendingTransition, setShowMcatChatErrorModal } = Auth.actions;
export const authReducer = Auth.reducer;
